import React, { useState } from "react";
import Layout from "../components/Layout";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { User, Key, Check, AlertCircle } from "lucide-react";

export default function ProfilePage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (newPass !== confirm) { setError("New passwords do not match"); return; }
    if (newPass.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPass });
      setSuccess("Password changed successfully");
      setCurrent(""); setNewPass(""); setConfirm("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = user?.role === "superadmin" ? "Super Administrator" : user?.role === "dept_admin" ? "Department Admin" : "Analyst";

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "560px" }}>
        <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", marginBottom: "0.25rem" }}>Profile</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: "2rem" }}>Manage your account settings</p>

        {/* Profile info card */}
        <div className="recon-card" style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
            <div style={{
              width: "3.5rem", height: "3.5rem", borderRadius: "9999px",
              background: "var(--sage-light)", border: "2px solid var(--sage-border)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <span style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--sage)", fontFamily: "Bricolage Grotesque, sans-serif" }}>
                {(user?.name || "U")[0].toUpperCase()}
              </span>
            </div>
            <div>
              <p style={{ fontWeight: 600, fontSize: "1.1rem", color: "var(--text-primary)", margin: 0, fontFamily: "Bricolage Grotesque, sans-serif" }}>{user?.name}</p>
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>{user?.email}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <div style={{ padding: "0.625rem 1rem", background: "var(--bg-default)", borderRadius: "0.5rem", border: "1px solid var(--border-default)" }}>
              <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", margin: "0 0 0.125rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Role</p>
              <p style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-primary)", margin: 0 }}>{roleLabel}</p>
            </div>
            {user?.department_id && (
              <div style={{ padding: "0.625rem 1rem", background: "var(--bg-default)", borderRadius: "0.5rem", border: "1px solid var(--border-default)" }}>
                <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", margin: "0 0 0.125rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Department</p>
                <p style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-primary)", margin: 0 }}>Assigned</p>
              </div>
            )}
          </div>
        </div>

        {/* Change password */}
        <div className="recon-card" style={{ padding: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
            <Key size={18} style={{ color: "var(--sage)" }} strokeWidth={1.5} />
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", color: "var(--text-primary)", margin: 0 }}>Change Password</h3>
          </div>

          {success && (
            <div data-testid="pw-success" style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: "0.5rem", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <Check size={16} style={{ color: "#065F46" }} />
              <p style={{ fontSize: "0.875rem", color: "#065F46", margin: 0 }}>{success}</p>
            </div>
          )}
          {error && (
            <div data-testid="pw-error" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <AlertCircle size={16} style={{ color: "#DC2626" }} />
              <p style={{ fontSize: "0.875rem", color: "#991B1B", margin: 0 }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleChangePassword} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {[
              { label: "Current Password", value: current, setter: setCurrent, testid: "current-pw" },
              { label: "New Password", value: newPass, setter: setNewPass, testid: "new-pw" },
              { label: "Confirm New Password", value: confirm, setter: setConfirm, testid: "confirm-pw" },
            ].map(f => (
              <div key={f.label}>
                <label style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>{f.label}</label>
                <input data-testid={f.testid} type="password" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
                  value={f.value} onChange={e => f.setter(e.target.value)} required />
              </div>
            ))}
            <button data-testid="change-pw-btn" type="submit" className="btn-primary" disabled={saving}
              style={{ width: "fit-content", opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
